import { useMemo, useState } from 'react'
import { NewBook } from '@/components/BooksSection'
import { FormError } from '@/components/FormError'
import { RecipeTile } from '@/components/RecipeTile'
import { PickedBar } from '@/components/Selection'
import { SendRecipes } from '@/components/SendTo'
import type { Family, Space } from '@/lib/families'
import type { Selection } from '@/lib/selection'
import { matching, NO_BOOK, recipeGroups, useAllRecipes, type RecipeSummary, type RecipesBy } from '@/lib/recipes'

const plural = (n: number) => `${n} ${n === 1 ? 'recipe' : 'recipes'}`

// Every recipe you can see, all at once instead of a book at a time: newest first, or under letters, meals or books,
// matching what's typed in the page's search box. With `mine`, only the recipes you added; with `loose`, only those
// in no book. Any of them can be picked to send to a book together.
export function AllRecipes({
  family,
  space,
  by,
  mine,
  loose,
  search = '',
  selection,
}: {
  family: Family
  space: Space | null
  by: RecipesBy
  mine: boolean
  search?: string
  loose?: boolean
  selection: Selection
}) {
  const recipes = useAllRecipes(family.id, mine, space?.id)
  const all = useMemo(
    () => (loose ? recipes.data?.recipes.filter((x) => !x.book) : recipes.data?.recipes),
    [recipes.data, loose],
  )
  const groups = useMemo(() => recipeGroups(matching(all ?? [], search), by), [all, search, by])
  // Under a book's heading, the line under each recipe says who added it; elsewhere, which book it's in.
  const line = (recipe: RecipeSummary) => (by === 'book' || loose ? recipe.by : (recipe.book?.title ?? NO_BOOK))

  return (
    <div className="flex flex-col gap-10">
      <FormError error={recipes.error} />
      {all && groups.length === 0 && (
        <p className="text-muted-foreground">
          {all.length > 0
            ? `No recipes match “${search.trim()}”.`
            : loose
              ? 'Every recipe is in a book.'
              : mine
                ? 'You haven’t added any recipes yet.'
                : 'No recipes yet. Add the first from the “+”.'}
        </p>
      )}
      {groups.map((group) => (
        <section
          key={group.key}
          className="flex flex-col gap-6"
          aria-labelledby={group.label ? `recipes-${by}-${group.key}` : undefined}
          aria-label={group.label ? undefined : 'All recipes'}
        >
          {group.label && (
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h2 id={`recipes-${by}-${group.key}`} className="text-title">
                {group.label}
              </h2>
              <span className="text-muted-foreground">{plural(group.recipes.length)}</span>
            </div>
          )}
          <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:grid-cols-6">
            {group.recipes.map((recipe, i) => (
              <li key={recipe.id}>
                <RecipeTile
                  recipe={recipe}
                  index={i}
                  line={line(recipe)}
                  picked={selection.selected.has(recipe.id)}
                  onToggle={selection.selecting ? () => selection.toggle(recipe.id) : undefined}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
      <SendPicked family={family} space={space} selection={selection} recipes={all ?? []} />
    </div>
  )
}

// While picking: the bar that sends the picked recipes to a book. Only whoever added a recipe can send it.
function SendPicked({
  family,
  space,
  selection,
  recipes,
}: {
  family: Family
  space: Space | null
  selection: Selection
  recipes: RecipeSummary[]
}) {
  const [sending, setSending] = useState(false)
  const [starting, setStarting] = useState(false)
  if (!selection.selecting) return null
  const picked = recipes.filter((x) => selection.selected.has(x.id))
  const mine = picked.every((x) => x.mine)
  return (
    <>
      <PickedBar
        label="Send the recipes you picked"
        count={picked.length}
        noun="recipe"
        actions={[{ label: 'Send to book', onSelect: () => setSending(true), disabled: !mine }]}
        onPickAll={() => selection.pickAll(recipes.map((x) => x.id))}
        onDone={selection.stop}
      />
      {picked.length > 0 && !mine && (
        <p role="status" className="sr-only">
          Only recipes you added can be sent to a book.
        </p>
      )}
      <SendRecipes
        open={sending && !starting}
        onOpenChange={setSending}
        familyId={family.id}
        recipes={picked.map((x) => x.id)}
        inBook={picked.some((x) => x.book)}
        onSent={() => {
          setSending(false)
          selection.stop()
        }}
        onNewBook={() => setStarting(true)}
      />
      {starting && (
        <NewBook
          open
          onOpenChange={(open) => !open && setStarting(false)}
          family={family}
          space={space}
          first={false}
          send={picked.map((x) => x.id)}
        />
      )}
    </>
  )
}
